/**
 * Product Customizations Manager
 * Monitors upholstery, corian, and stain selections and updates hidden form attributes
 * Also handles disabling add to cart button when required options are not selected
 */
class ProductCustomizationsManager {
  constructor() {
    this.form = null;
    this.stainInput = null;
    this.upholsteryInput = null;
    this.fabricInput = null;
    this.corianInput = null;
    this.addToCartButton = null;
    this.hasStainPicker = false;
    this.hasUpholsteryPicker = false;
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    // Find the main product form
    this.form = document.querySelector('form[is="product-form"]');
    if (!this.form) {
      console.warn('ProductCustomizationsManager: Product form not found');
      return;
    }
    
    // Find the hidden inputs
    this.stainInput = document.getElementById('dynamic-stain-input');
    this.upholsteryInput = document.getElementById('dynamic-upholstery-input');
    this.fabricInput = document.getElementById('fabric-type');
    this.corianInput = document.getElementById('dynamic-corian-input');
    
    // Find the add to cart button
    this.addToCartButton = this.form.querySelector('button[name="add"]');
    
    // Check if stain picker exists on the page
    this.hasStainPicker = document.querySelector('.color-stain-picker') !== null;
    
    // Check if upholstery picker exists on the page
    this.hasUpholsteryPicker = document.querySelector('.upholstery-picker') !== null;
    
    // Store reference to upholstery picker container for hiding/showing
    this.upholsteryPickerContainer = document.querySelector('.upholstery-picker');
    
    if (!this.stainInput || !this.upholsteryInput || !this.corianInput) {
      console.warn('ProductCustomizationsManager: One or more hidden inputs not found');
      return;
    }
    
    // Set up event listeners
    this.setupStainListener();
    this.setupUpholsteryListener();
    this.setupFabricListener();
    this.setupSeatOptionListener();
    this.setupCorianListener();
    this.setupVariantChangeListener();
    this.setupStainRequiredListener();
    
    // Set initial values
    this.updateAllSelections();
    
    // Check initial seat option and upholstery visibility
    this.updateUpholsteryVisibility();
    
    // Check initial button state
    this.updateAddToCartButtonState();
    
    console.log('ProductCustomizationsManager: Initialized successfully');
  }
  
  setupStainListener() {
    // Listen for stain selection changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="properties[Stain]"]')) {
        this.updateStainSelection(event.target.value);
        this.updateAddToCartButtonState();
      }
    });
    
    // Also listen for clicks on stain labels
    document.addEventListener('click', (event) => {
      if (event.target.matches('label[for*="stain"]') || 
          event.target.closest('.color-stain-picker .swatch-element')) {
        setTimeout(() => {
          this.updateStainSelection();
          this.updateAddToCartButtonState();
        }, 100);
      }
    });
  }
  
  setupUpholsteryListener() {
    // Listen for upholstery selection changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="properties[Upholstery Option]"]')) {
        this.updateUpholsterySelection(event.target.value);
        this.updateAddToCartButtonState();
      }
    });
    
    // Also listen for clicks on upholstery labels
    document.addEventListener('click', (event) => {
      if (event.target.matches('label[for*="leather"]') || 
          event.target.closest('.upholstery-picker .swatch-element')) {
        setTimeout(() => {
          this.updateUpholsterySelection();
          this.updateAddToCartButtonState();
        }, 100);
      }
    });
  }
  
  setupFabricListener() {
    // Listen for fabric input changes
    document.addEventListener('input', (event) => {
      if (event.target.id === 'fabric-type') {
        this.updateAddToCartButtonState();
      }
    });
    
    // Also listen for blur event to catch final value
    document.addEventListener('blur', (event) => {
      if (event.target.id === 'fabric-type') {
        this.updateAddToCartButtonState();
      }
    });
  }
  
  setupSeatOptionListener() {
    // Listen for seat option changes
    document.addEventListener('change', (event) => {
      if (event.target.type === 'radio') {
        const name = event.target.name.toLowerCase();
        // Check if this is a seat option field
        if (name.includes('seat') || name.includes('option')) {
          this.updateUpholsteryVisibility();
          this.updateAddToCartButtonState();
        }
      }
    });
    
    // Also listen for clicks on seat option labels/buttons
    document.addEventListener('click', (event) => {
      if (event.target.type === 'radio') {
        const name = event.target.name.toLowerCase();
        if (name.includes('seat') || name.includes('option')) {
          setTimeout(() => {
            this.updateUpholsteryVisibility();
            this.updateAddToCartButtonState();
          }, 50);
        }
      }
    });
  }
  
  setupCorianListener() {
    // Listen for corian selection changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="properties[Surface Option]"]')) {
        this.updateCorianSelection(event.target.value);
      }
    });
    
    // Also listen for clicks on corian labels
    document.addEventListener('click', (event) => {
      if (event.target.matches('label[for*="corian"]') || 
          event.target.closest('.corian-picker .swatch-element')) {
        setTimeout(() => this.updateCorianSelection(), 100);
      }
    });
  }
  
  setupVariantChangeListener() {
    // Listen for variant changes from the theme's pub/sub system
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        // Small delay only if stain was already selected (to let variant system update first)
        // But if no stain selected, run immediately to prevent flicker
        const hasStainValue = this.stainInput && this.stainInput.value && this.stainInput.value.trim() !== '';
        
        if (hasStainValue) {
          setTimeout(() => {
            this.updateAddToCartButtonState();
          }, 50);
        } else {
          this.updateAddToCartButtonState();
        }
      });
    }
    
    // Also listen for the variant:changed custom event as a fallback
    document.addEventListener('variant:changed', (event) => {
      // Same logic as above
      const hasStainValue = this.stainInput && this.stainInput.value && this.stainInput.value.trim() !== '';
      
      if (hasStainValue) {
        setTimeout(() => {
          this.updateAddToCartButtonState();
        }, 50);
      } else {
        this.updateAddToCartButtonState();
      }
    });
  }
  
  // **NEW: Listen for stain required/not-required events from stain-swatches.js**
  setupStainRequiredListener() {
    document.addEventListener('stain:not-required', (event) => {
      this.updateAddToCartButtonState();
    });
    
    document.addEventListener('stain:required', (event) => {
      this.updateAddToCartButtonState();
    });
  }
  
  updateStainSelection(value = null) {
    if (!this.stainInput) return;
    
    let selectedValue = value;
    if (!selectedValue) {
      const selectedStain = document.querySelector('input[name="properties[Stain]"]:checked');
      selectedValue = selectedStain ? selectedStain.value : '';
    }
    
    this.stainInput.value = selectedValue;
  }
  
  updateUpholsterySelection(value = null) {
    if (!this.upholsteryInput) return;
    
    let selectedValue = value;
    if (!selectedValue) {
      const selectedUpholstery = document.querySelector('input[name="properties[Upholstery Option]"]:checked');
      selectedValue = selectedUpholstery ? selectedUpholstery.value : '';
    }
    
    this.upholsteryInput.value = selectedValue;
  }
  
  updateCorianSelection(value = null) {
    if (!this.corianInput) return;
    
    let selectedValue = value;
    if (!selectedValue) {
      const selectedCorian = document.querySelector('input[name="properties[Surface Option]"]:checked');
      selectedValue = selectedCorian ? selectedCorian.value : '';
    }
    
    this.corianInput.value = selectedValue;
  }
  
  updateAllSelections() {
    this.updateStainSelection();
    this.updateUpholsterySelection();
    this.updateCorianSelection();
  }
  
  // Public method to get current selections
  getCurrentSelections() {
    return {
      stain: this.stainInput ? this.stainInput.value : '',
      upholstery: this.upholsteryInput ? this.upholsteryInput.value : '',
      corian: this.corianInput ? this.corianInput.value : ''
    };
  }
  
  // Public method to set selections programmatically
  setSelections(selections) {
    if (selections.stain !== undefined) {
      this.updateStainSelection(selections.stain);
    }
    if (selections.upholstery !== undefined) {
      this.updateUpholsterySelection(selections.upholstery);
    }
    if (selections.corian !== undefined) {
      this.updateCorianSelection(selections.corian);
    }
    this.updateAddToCartButtonState();
  }
  
  // Get the current selected seat option
  getCurrentSeatOption() {
    // Look for any checked radio button that might be a seat option
    const allRadios = document.querySelectorAll('input[type="radio"]:checked');
    
    for (const radio of allRadios) {
      const name = radio.name.toLowerCase();
      // Check if this looks like a seat option field
      if (name.includes('seat') || name.includes('option')) {
        return radio.value;
      }
    }
    
    return null;
  }
  
  // Update upholstery picker visibility based on seat option
  updateUpholsteryVisibility() {
    if (!this.upholsteryPickerContainer) {
      return;
    }
    
    const currentSeatOption = this.getCurrentSeatOption();
    
    if (!currentSeatOption) {
      return;
    }
    
    const seatOptionLower = currentSeatOption.toLowerCase();
    
    // Show upholstery picker only if seat option contains "leather" or "fabric"
    if (seatOptionLower.includes('leather') || seatOptionLower.includes('fabric')) {
      this.upholsteryPickerContainer.style.display = 'block';
    } else {
      // Hide upholstery picker for wood seats or other non-upholstery options
      this.upholsteryPickerContainer.style.display = 'none';
      
      // Clear upholstery and fabric selections when hiding
      if (this.upholsteryInput) this.upholsteryInput.value = '';
      if (this.fabricInput) this.fabricInput.value = '';
    }
  }
  
  // Check if upholstery is currently required (visible and applicable)
  isUpholsteryRequired() {
    if (!this.hasUpholsteryPicker || !this.upholsteryPickerContainer) {
      return false;
    }
    
    // Only require upholstery if the picker is visible
    const isVisible = this.upholsteryPickerContainer.style.display !== 'none';
    
    return isVisible;
  }
  
  // **UPDATED: Check if stain is currently required**
  isStainRequired() {
    if (!this.hasStainPicker) {
      return false;
    }
    
    // Check if stain picker is marked as not required (e.g., "As Shown" wood type)
    const stainPicker = document.querySelector('.color-stain-picker');
    if (stainPicker) {
      // Check if picker is hidden via display style
      const isDisplayNone = window.getComputedStyle(stainPicker).display === 'none';
      
      // Check if picker has attributes marking it as not required
      const isHidden = stainPicker.hasAttribute('data-hidden');
      const noStainRequired = stainPicker.hasAttribute('data-no-stain-required');
      
      if (isDisplayNone || isHidden || noStainRequired) {
        return false;
      }
    }
    
    // Also check the hidden input for the data-no-validation attribute
    if (this.stainInput && this.stainInput.hasAttribute('data-no-validation')) {
      return false;
    }
    
    return true;
  }
  
  // Update the state of the Add to Cart button based on required selections
  updateAddToCartButtonState() {
    if (!this.addToCartButton) {
      return;
    }
    
    // **UPDATED: Check if stain is required and selected**
    const stainRequired = this.isStainRequired();
    const hasStainValue = this.stainInput && this.stainInput.value && this.stainInput.value.trim() !== '';
    const stainNotNeeded = this.stainInput && this.stainInput.hasAttribute('data-no-validation');
    const stainSelected = hasStainValue || stainNotNeeded;
    
    // Check if upholstery is required but not selected
    const upholsteryRequired = this.isUpholsteryRequired();
    const leatherSelected = this.upholsteryInput && this.upholsteryInput.value && this.upholsteryInput.value.trim() !== '';
    const fabricEntered = this.fabricInput && this.fabricInput.value && this.fabricInput.value.trim() !== '';
    const upholsterySelected = leatherSelected || fabricEntered;
    
    // Determine if button should be disabled due to missing selections
    const shouldDisable = (stainRequired && !stainSelected) || (upholsteryRequired && !upholsterySelected);
    
    // Get button text element
    const buttonTextElement = this.addToCartButton.querySelector('span');
    
    if (shouldDisable) {
      // Only store original state if this is the FIRST time we're disabling it
      if (!this.addToCartButton.hasAttribute('data-disabled-by-customizations')) {
        // Store the button state BEFORE we disable it
        const currentlyDisabled = this.addToCartButton.disabled;
        const currentText = buttonTextElement ? buttonTextElement.textContent.trim() : '';
        
        this.addToCartButton.setAttribute('data-original-disabled-state', currentlyDisabled);
        this.addToCartButton.setAttribute('data-original-text', currentText);
      }
      
      // Disable button and update text
      this.addToCartButton.disabled = true;
      this.addToCartButton.setAttribute('data-disabled-by-customizations', 'true');
      
      if (buttonTextElement) {
        // Determine which options are missing
        const missingOptions = [];
        if (stainRequired && !stainSelected) missingOptions.push('stain');
        if (upholsteryRequired && !upholsterySelected) missingOptions.push('upholstery');
        
        // Update button text
        buttonTextElement.textContent = `Select ${missingOptions.join(' and ')}`;
      }
    } else {
      // Check if the button was disabled by us
      const wasDisabledByCustomizations = this.addToCartButton.hasAttribute('data-disabled-by-customizations');
      
      if (wasDisabledByCustomizations) {
        // Remove our customization flag
        this.addToCartButton.removeAttribute('data-disabled-by-customizations');
        
        // Restore the original disabled state
        const originalDisabledState = this.addToCartButton.getAttribute('data-original-disabled-state');
        const originalText = this.addToCartButton.getAttribute('data-original-text');
        
        // Restore button state
        if (originalDisabledState !== null) {
          this.addToCartButton.disabled = originalDisabledState === 'true';
        } else {
          this.addToCartButton.disabled = false;
        }
        
        // Restore button text
        if (buttonTextElement && originalText) {
          buttonTextElement.textContent = originalText;
        }
        
        // Clean up attributes
        this.addToCartButton.removeAttribute('data-original-disabled-state');
        this.addToCartButton.removeAttribute('data-original-text');
      }
    }
  }
}

// Initialize the manager
const productCustomizationsManager = new ProductCustomizationsManager();

// Make it globally available for debugging
window.ProductCustomizationsManager = productCustomizationsManager;